import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { BadgePercent, Plus, Trash2 } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { POSVisualKeyboardField, POSVisualNumpadField } from './POSVisualInput'
import { MenuImageField } from './MenuImageField'
import {
  useMenuCategories,
  useMenuItems,
  useCreateMenuItem,
  useUpdateMenuItem,
  useCreateMenuItemVariant,
  useDeleteMenuItemVariant,
  useCreateMenuModifier,
  useDeleteMenuModifier,
  useDietaryTags,
} from '@/lib/queries/usePOS'
import { formatPOSMoney, usePOSCurrency } from '@/lib/pos/currency'
import type { MenuItem } from '@/lib/types/pos'

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  categoryDocumentId: z.string().optional(),
  basePrice: z.coerce.number().min(0, 'Price must be ≥ 0'),
  soldByWeight: z.boolean(),
  estimatedWeightGramsPerPiece: z.coerce.number().int().min(0).optional().nullable(),
  taxClass: z.enum(['standard', 'reduced', 'zero']),
  prepStation: z.enum(['kitchen', 'bar', 'grill', 'cold', 'none']),
  isAvailable: z.boolean(),
  stockTracking: z.boolean(),
  stockCount: z.coerce.number().int().min(0).optional().nullable(),
  imageUrl: z.string().optional(),
  sortOrder: z.coerce.number().int().default(0),
  orderRole: z.enum(['standalone', 'child_only']),
})

type FormInput = z.input<typeof schema>
type FormData = z.output<typeof schema>

interface Props {
  open: boolean
  onClose: () => void
  item?: MenuItem | null
  defaultCategoryDocumentId?: string | null
}

const PROMOTION_DAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
]

function dateInputValue(value: string | null | undefined) {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''
  const year = parsed.getFullYear()
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const day = String(parsed.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function dateToIsoBoundary(value: string, boundary: 'start' | 'end') {
  if (!value) return undefined
  const suffix = boundary === 'start' ? 'T00:00:00' : 'T23:59:59'
  const parsed = new Date(`${value}${suffix}`)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString()
}

export function MenuItemFormModal({ open, onClose, item, defaultCategoryDocumentId = null }: Props) {
  const isEdit = !!item
  const currency = usePOSCurrency()
  const money = (amount: number) => formatPOSMoney(amount, currency)

  const { data: catsResp } = useMenuCategories()
  const categories = (catsResp as any)?.data ?? []
  const { data: allItemsResp } = useMenuItems()
  const allItems: MenuItem[] = (allItemsResp as any)?.data ?? []
  const { data: dietaryTagsResp } = useDietaryTags()
  const dietaryTags = (dietaryTagsResp as any)?.data ?? []

  const createItem = useCreateMenuItem()
  const updateItem = useUpdateMenuItem()
  const createVariant = useCreateMenuItemVariant()
  const deleteVariant = useDeleteMenuItemVariant()
  const createModifier = useCreateMenuModifier()
  const deleteModifier = useDeleteMenuModifier()

  // New variant/modifier form state
  const [newVariantName, setNewVariantName] = useState('')
  const [newVariantDelta, setNewVariantDelta] = useState('')
  const [newModifierName, setNewModifierName] = useState('')
  const [newModifierDelta, setNewModifierDelta] = useState('')
  const [extraCategoryIds, setExtraCategoryIds] = useState<string[]>([])
  const [extraItemIds, setExtraItemIds] = useState<string[]>([])
  const [dietaryTagIds, setDietaryTagIds] = useState<string[]>([])
  const [promotionEnabled, setPromotionEnabled] = useState(false)
  const [promotionPrice, setPromotionPrice] = useState('')
  const [promotionLabel, setPromotionLabel] = useState('')
  const [promotionDays, setPromotionDays] = useState<number[]>([])
  const [promotionStartsAt, setPromotionStartsAt] = useState('')
  const [promotionEndsAt, setPromotionEndsAt] = useState('')
  const [promotionError, setPromotionError] = useState('')

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormInput, any, FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      categoryDocumentId: '',
      basePrice: 0,
      soldByWeight: false,
      estimatedWeightGramsPerPiece: null,
      taxClass: 'standard',
      prepStation: 'kitchen',
      isAvailable: true,
      stockTracking: false,
      stockCount: null,
      imageUrl: '',
      sortOrder: 0,
      orderRole: 'standalone',
    },
  })

  const stockTracking = watch('stockTracking')
  const name = watch('name')
  const description = watch('description')
  const basePrice = watch('basePrice')
  const soldByWeight = watch('soldByWeight')
  const estimatedWeightGramsPerPiece = watch('estimatedWeightGramsPerPiece')
  const sortOrder = watch('sortOrder')
  const stockCount = watch('stockCount')
  const orderRole = watch('orderRole')
  const imageUrl = watch('imageUrl')

  useEffect(() => {
    if (open && item) {
      const promotionRule = item.promotionRules?.[0]
      reset({
        name: item.name,
        description: item.description ?? '',
        categoryDocumentId: item.category?.documentId ?? '',
        basePrice: item.regularBasePrice ?? item.basePrice,
        soldByWeight: item.soldByWeight === true,
        estimatedWeightGramsPerPiece: item.estimatedWeightGramsPerPiece ?? null,
        taxClass: item.taxClass,
        prepStation: item.prepStation,
        isAvailable: item.isAvailable,
        stockTracking: item.stockTracking,
        stockCount: item.stockCount ?? null,
        imageUrl: item.imageUrl ?? '',
        sortOrder: item.sortOrder,
        orderRole: item.orderRole === 'child_only' ? 'child_only' : 'standalone',
      })
      setExtraCategoryIds(item.extraAppliesToCategories ?? [])
      setExtraItemIds(item.extraAppliesToItems ?? [])
      setDietaryTagIds((item.dietaryTags ?? []).map((tag) => tag.documentId))
      setPromotionEnabled(!!promotionRule)
      setPromotionPrice(promotionRule?.price == null ? '' : String(promotionRule.price))
      setPromotionLabel(promotionRule?.label ?? '')
      setPromotionDays(promotionRule?.days ?? [])
      setPromotionStartsAt(dateInputValue(promotionRule?.startsAt))
      setPromotionEndsAt(dateInputValue(promotionRule?.endsAt))
      setPromotionError('')
    } else if (open && !item) {
      reset({
        name: '',
        description: '',
        categoryDocumentId: defaultCategoryDocumentId ?? '',
        basePrice: 0,
        soldByWeight: false,
        estimatedWeightGramsPerPiece: null,
        taxClass: 'standard',
        prepStation: 'kitchen',
        isAvailable: true,
        stockTracking: false,
        stockCount: null,
        imageUrl: '',
        sortOrder: 0,
        orderRole: 'standalone',
      })
      setExtraCategoryIds([])
      setExtraItemIds([])
      setDietaryTagIds([])
      setPromotionEnabled(false)
      setPromotionPrice('')
      setPromotionLabel('')
      setPromotionDays([])
      setPromotionStartsAt('')
      setPromotionEndsAt('')
      setPromotionError('')
    }
  }, [open, item, defaultCategoryDocumentId, reset])

  const onSubmit = async (data: FormData) => {
    const isChildOnly = data.orderRole === 'child_only'
    const promotionRules = buildPromotionRules(data.basePrice)
    if (!promotionRules) return

    if (isEdit) {
      await updateItem.mutateAsync({
        documentId: item!.documentId,
        name: data.name,
        description: data.description,
        basePrice: data.basePrice,
        soldByWeight: data.soldByWeight,
        estimatedWeightGramsPerPiece: data.soldByWeight && Number(data.estimatedWeightGramsPerPiece) > 0 ? Number(data.estimatedWeightGramsPerPiece) : null,
        taxClass: data.taxClass,
        prepStation: data.prepStation,
        isAvailable: data.isAvailable,
        stockTracking: data.stockTracking,
        stockCount: data.stockTracking ? (data.stockCount ?? null) : null,
        imageUrl: data.imageUrl || null,
        sortOrder: data.sortOrder,
        orderRole: data.orderRole,
        extraAppliesToCategories: isChildOnly ? extraCategoryIds : [],
        extraAppliesToItems: isChildOnly ? extraItemIds : [],
        promotionRules,
        dietaryTagDocumentIds: dietaryTagIds,
        categoryDocumentId: data.categoryDocumentId || null,
      })
    } else {
      await createItem.mutateAsync({
        name: data.name,
        description: data.description,
        basePrice: data.basePrice,
        soldByWeight: data.soldByWeight,
        estimatedWeightGramsPerPiece: data.soldByWeight && Number(data.estimatedWeightGramsPerPiece) > 0 ? Number(data.estimatedWeightGramsPerPiece) : null,
        taxClass: data.taxClass,
        prepStation: data.prepStation,
        isAvailable: data.isAvailable,
        stockTracking: data.stockTracking,
        stockCount: data.stockTracking ? (data.stockCount ?? null) : null,
        imageUrl: data.imageUrl || null,
        sortOrder: data.sortOrder,
        orderRole: data.orderRole,
        extraAppliesToCategories: isChildOnly ? extraCategoryIds : [],
        extraAppliesToItems: isChildOnly ? extraItemIds : [],
        promotionRules,
        dietaryTagDocumentIds: dietaryTagIds,
        categoryDocumentId: data.categoryDocumentId || undefined,
      })
    }
    onClose()
  }

  const handleAddVariant = async () => {
    if (!item || !newVariantName.trim()) return
    const delta = parseFloat(newVariantDelta) || 0
    await createVariant.mutateAsync({
      menuItemDocumentId: item.documentId,
      name: newVariantName.trim(),
      priceDelta: delta,
    })
    setNewVariantName('')
    setNewVariantDelta('')
  }

  const handleAddModifier = async () => {
    if (!item || !newModifierName.trim()) return
    const delta = parseFloat(newModifierDelta) || 0
    await createModifier.mutateAsync({
      menuItemDocumentId: item.documentId,
      name: newModifierName.trim(),
      priceDelta: delta,
    })
    setNewModifierName('')
    setNewModifierDelta('')
  }

  const categoryOptions = [
    { value: '', label: '— No category —' },
    ...categories.map((c: any) => ({ value: c.documentId, label: c.name })),
  ]
  const parentItemOptions = allItems
    .filter((candidate) => candidate.documentId !== item?.documentId)
    .filter((candidate) => candidate.orderRole !== 'child_only')

  const toggleArrayValue = (value: string, current: string[], setNext: (next: string[]) => void) => {
    setNext(current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value])
  }

  const togglePromotionDay = (day: number) => {
    setPromotionDays((current) =>
      current.includes(day)
        ? current.filter((entry) => entry !== day)
        : [...current, day].sort((a, b) => a - b),
    )
  }

  const buildPromotionRules = (regularPrice: number) => {
    if (!promotionEnabled) {
      setPromotionError('')
      return []
    }

    const price = Number(promotionPrice)
    if (!Number.isFinite(price) || price <= 0) {
      setPromotionError('Enter a fixed promotional price.')
      return null
    }
    if (price >= regularPrice) {
      setPromotionError('Promotional price must be lower than the base price.')
      return null
    }
    if (promotionStartsAt && promotionEndsAt && promotionStartsAt > promotionEndsAt) {
      setPromotionError('End date must be after the start date.')
      return null
    }

    setPromotionError('')
    return [{
      price,
      days: promotionDays,
      startsAt: dateToIsoBoundary(promotionStartsAt, 'start'),
      endsAt: dateToIsoBoundary(promotionEndsAt, 'end'),
      carryOverUntil: item?.promotionRules?.[0]?.carryOverUntil ?? undefined,
      label: promotionLabel.trim() || undefined,
    }]
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Item' : 'Add Item'}
      size="lg"
      className="sm:max-h-[90vh]"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Basic info */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="col-span-2">
            <POSVisualKeyboardField
              label="Name"
              value={name ?? ''}
              onChange={(value) => setValue('name', value, { shouldDirty: true, shouldValidate: true })}
            />
            {errors.name?.message && (
              <p className="mt-1 text-xs text-[var(--color-accent-red)]">{errors.name.message}</p>
            )}
          </div>
          <div className="col-span-2">
            <POSVisualKeyboardField
              label="Description"
              value={description ?? ''}
              onChange={(value) => setValue('description', value, { shouldDirty: true, shouldValidate: true })}
              multiline
            />
          </div>
          <POSVisualNumpadField
            label={soldByWeight ? `Price per kg (${currency})` : `Base Price (${currency})`}
            value={String(basePrice ?? '')}
            onChange={(value) => setValue('basePrice', value as any, { shouldDirty: true, shouldValidate: true })}
            prefix={currency}
          />
          {errors.basePrice?.message && (
            <p className="text-xs text-[var(--color-accent-red)]">{errors.basePrice.message}</p>
          )}
          <label className="col-span-2 flex items-start gap-2 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3 text-sm">
            <input type="checkbox" {...register('soldByWeight')} className="mt-0.5 rounded" />
            <span>
              <span className="block font-medium text-[var(--color-text-primary)]">Sold by weight</span>
              <span className="mt-0.5 block text-xs text-[var(--color-text-muted)]">Customers choose grams or kilograms. Set the price per kilogram above.</span>
            </span>
          </label>
          {soldByWeight && (
            <POSVisualNumpadField
              label="Typical grams per piece (AI estimate, optional)"
              value={String(estimatedWeightGramsPerPiece ?? '')}
              onChange={(value) => setValue('estimatedWeightGramsPerPiece', value === '' ? null : Number(value), { shouldDirty: true, shouldValidate: true })}
              mode="integer"
              placeholder="e.g. 180"
              className="col-span-2"
            />
          )}
          <Select
            label="Category"
            options={categoryOptions}
            {...register('categoryDocumentId')}
          />
          <Select
            label="Tax Class"
            options={[
              { value: 'standard', label: 'Standard (20%)' },
              { value: 'reduced', label: 'Reduced (10%)' },
              { value: 'zero', label: 'Zero (0%)' },
            ]}
            {...register('taxClass')}
          />
          <Select
            label="Prep Station"
            options={[
              { value: 'kitchen', label: 'Kitchen' },
              { value: 'bar', label: 'Bar' },
              { value: 'grill', label: 'Grill' },
              { value: 'cold', label: 'Cold' },
              { value: 'none', label: 'None' },
            ]}
            {...register('prepStation')}
          />
          <POSVisualNumpadField
            label="Sort Order"
            value={String(sortOrder ?? '')}
            onChange={(value) => setValue('sortOrder', value as any, { shouldDirty: true, shouldValidate: true })}
            mode="integer"
          />
          <div className="col-span-2">
            <MenuImageField
              label="Item image"
              value={imageUrl ?? ''}
              onChange={(value) => setValue('imageUrl', value, { shouldDirty: true, shouldValidate: true })}
            />
          </div>
        </div>

        <div className="space-y-3 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={promotionEnabled}
              onChange={(event) => {
                setPromotionEnabled(event.target.checked)
                setPromotionError('')
              }}
              className="mt-1 rounded"
            />
            <span className="min-w-0">
              <span className="flex items-center gap-2 text-sm font-medium text-[var(--color-text-primary)]">
                <BadgePercent className="h-4 w-4 text-[var(--color-accent-amber)]" />
                Fixed promotional price
              </span>
              <span className="block text-xs text-[var(--color-text-muted)]">
                Applies an active fixed price to this item on selected days or dates.
              </span>
            </span>
          </label>

          {promotionEnabled && (
            <div className="grid gap-3 md:grid-cols-2">
              <POSVisualNumpadField
                label={`Promo Price (${currency})`}
                value={promotionPrice}
                onChange={setPromotionPrice}
                prefix={currency}
              />
              <POSVisualKeyboardField
                label="Label"
                value={promotionLabel}
                onChange={setPromotionLabel}
                placeholder="e.g. Thursday lunch"
              />
              <div className="md:col-span-2">
                <p className="mb-2 text-xs font-semibold uppercase text-[var(--color-text-muted)]">Active days</p>
                <div className="flex flex-wrap gap-2">
                  {PROMOTION_DAYS.map((day) => {
                    const active = promotionDays.includes(day.value)
                    return (
                      <button
                        key={day.value}
                        type="button"
                        aria-pressed={active}
                        onClick={() => togglePromotionDay(day.value)}
                        className={[
                          'h-8 rounded-[var(--radius-sm)] border px-3 text-xs font-semibold transition-colors',
                          active
                            ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber)] text-black'
                            : 'border-[var(--color-border-default)] bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-strong)]',
                        ].join(' ')}
                      >
                        {day.label}
                      </button>
                    )
                  })}
                </div>
              </div>
              <label className="space-y-1 text-xs font-medium text-[var(--color-text-muted)]">
                <span>Starts</span>
                <input
                  type="date"
                  value={promotionStartsAt}
                  onChange={(event) => setPromotionStartsAt(event.target.value)}
                  className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent-amber)]"
                />
              </label>
              <label className="space-y-1 text-xs font-medium text-[var(--color-text-muted)]">
                <span>Ends</span>
                <input
                  type="date"
                  value={promotionEndsAt}
                  onChange={(event) => setPromotionEndsAt(event.target.value)}
                  className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent-amber)]"
                />
              </label>
              {promotionError && (
                <p className="md:col-span-2 text-xs text-[var(--color-accent-red)]">{promotionError}</p>
              )}
            </div>
          )}
        </div>

        <div className="space-y-2 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3">
          <div>
            <p className="text-sm font-medium text-[var(--color-text-primary)]">Dietary and allergy information</p>
            <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
              Optional. Assign existing allergy or dietary tags to this item.
            </p>
          </div>
          {dietaryTags.length === 0 ? (
            <p className="text-xs text-[var(--color-text-muted)]">
              Create dietary tags from the menu's Dietary & allergies panel first.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {dietaryTags.map((tag: any) => {
                const checked = dietaryTagIds.includes(tag.documentId)
                return (
                  <button
                    key={tag.documentId}
                    type="button"
                    onClick={() => toggleArrayValue(tag.documentId, dietaryTagIds, setDietaryTagIds)}
                    className={`inline-flex h-8 items-center gap-2 rounded-full border px-3 text-xs font-medium transition-colors ${
                      checked
                        ? 'border-transparent text-white'
                        : 'border-[var(--color-border-default)] bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)] hover:border-[var(--color-accent-amber)]'
                    }`}
                    style={checked ? { backgroundColor: tag.color || '#B7791F' } : undefined}
                  >
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: checked ? 'rgba(255,255,255,.85)' : tag.color || '#B7791F' }}
                    />
                    {tag.name}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Toggles */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" {...register('isAvailable')} className="rounded" />
            <span className="text-sm text-[var(--color-text-secondary)]">Available</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" {...register('stockTracking')} className="rounded" />
            <span className="text-sm text-[var(--color-text-secondary)]">Track stock</span>
          </label>
          {stockTracking && (
            <div className="w-28">
              <POSVisualNumpadField
                label="Stock"
                value={stockCount == null ? '' : String(stockCount)}
                onChange={(value) => setValue('stockCount', value === '' ? null : value as any, { shouldDirty: true, shouldValidate: true })}
                mode="integer"
              />
            </div>
          )}
        </div>

        <div className="space-y-3 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={orderRole === 'child_only'}
              onChange={(event) => {
                setValue('orderRole', event.target.checked ? 'child_only' : 'standalone', {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }}
              className="mt-1 rounded"
            />
            <span>
              <span className="block text-sm font-medium text-[var(--color-text-primary)]">Extra only</span>
              <span className="block text-xs text-[var(--color-text-muted)]">
                This item can only be added under allowed parent items. Removals should be written as item notes.
              </span>
            </span>
          </label>

          {orderRole === 'child_only' && (
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase text-[var(--color-text-muted)]">Applies to categories</p>
                <div className="max-h-36 space-y-1 overflow-y-auto rounded-[var(--radius-sm)] border border-[var(--color-border-subtle)] p-2">
                  {categories.map((category: any) => (
                    <label key={category.documentId} className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
                      <input
                        type="checkbox"
                        checked={extraCategoryIds.includes(category.documentId)}
                        onChange={() => toggleArrayValue(category.documentId, extraCategoryIds, setExtraCategoryIds)}
                      />
                      <span>{category.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase text-[var(--color-text-muted)]">Applies to items</p>
                <div className="max-h-36 space-y-1 overflow-y-auto rounded-[var(--radius-sm)] border border-[var(--color-border-subtle)] p-2">
                  {parentItemOptions.map((parentItem) => (
                    <label key={parentItem.documentId} className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
                      <input
                        type="checkbox"
                        checked={extraItemIds.includes(parentItem.documentId)}
                        onChange={() => toggleArrayValue(parentItem.documentId, extraItemIds, setExtraItemIds)}
                      />
                      <span>{parentItem.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Variants section — only visible in edit mode */}
        {isEdit && (
          <div className="space-y-2 border-t border-[var(--color-border-subtle)] pt-4">
            <p className="text-sm font-medium text-[var(--color-text-secondary)]">Variants</p>
            {item!.variants.map((v) => (
              <div key={v.documentId} className="flex items-center justify-between text-sm py-1">
                <span className="text-[var(--color-text-primary)]">{v.name}</span>
                <div className="flex items-center gap-3">
                  <span className="text-[var(--color-text-muted)]">
                    {v.priceDelta >= 0 ? '+' : ''}{money(v.priceDelta)}
                  </span>
                  <button
                    type="button"
                    onClick={() => deleteVariant.mutate({ documentId: v.documentId, menuItemDocumentId: item!.documentId })}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-accent-red)] transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <POSVisualKeyboardField
                value={newVariantName}
                onChange={setNewVariantName}
                placeholder="Variant name"
                className="flex-1"
                inputClassName="h-8 text-sm"
              />
              <POSVisualNumpadField
                value={newVariantDelta}
                onChange={setNewVariantDelta}
                placeholder="±price"
                allowNegative
                prefix={currency}
                className="w-40"
                inputClassName="h-8 text-xs"
              />
              <button
                type="button"
                onClick={handleAddVariant}
                disabled={!newVariantName.trim() || createVariant.isPending}
                className="h-8 w-8 flex items-center justify-center rounded-[var(--radius-sm)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-default)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] disabled:opacity-40"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Modifiers section — only visible in edit mode */}
        {isEdit && (
          <div className="space-y-2 border-t border-[var(--color-border-subtle)] pt-4">
            <p className="text-sm font-medium text-[var(--color-text-secondary)]">Modifiers</p>
            {item!.modifiers.map((m) => (
              <div key={m.documentId} className="flex items-center justify-between text-sm py-1">
                <span className="text-[var(--color-text-primary)]">{m.name}</span>
                <div className="flex items-center gap-3">
                  <span className="text-[var(--color-text-muted)]">
                    {m.priceDelta >= 0 ? '+' : ''}{money(m.priceDelta)}
                  </span>
                  <button
                    type="button"
                    onClick={() => deleteModifier.mutate({ documentId: m.documentId, menuItemDocumentId: item!.documentId })}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-accent-red)] transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <POSVisualKeyboardField
                value={newModifierName}
                onChange={setNewModifierName}
                placeholder="Modifier name"
                className="flex-1"
                inputClassName="h-8 text-sm"
              />
              <POSVisualNumpadField
                value={newModifierDelta}
                onChange={setNewModifierDelta}
                placeholder="±price"
                allowNegative
                prefix={currency}
                className="w-40"
                inputClassName="h-8 text-xs"
              />
              <button
                type="button"
                onClick={handleAddModifier}
                disabled={!newModifierName.trim() || createModifier.isPending}
                className="h-8 w-8 flex items-center justify-center rounded-[var(--radius-sm)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-default)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] disabled:opacity-40"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-2 border-t border-[var(--color-border-subtle)]">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={isSubmitting}>{isEdit ? 'Save' : 'Create'}</Button>
        </div>
      </form>
    </Modal>
  )
}
